/**
 * Scenario Management Hooks
 * React Query hooks and utilities for scenario operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import type { 
  Scenario, 
  ScenarioCreate, 
  ScenarioUpdate,
  ScenarioWithDetails 
} from '../types/vrp';

// Re-export types that other modules might need
export type { ScenarioCreate } from '../types/vrp';

// =============================================
// BASIC SCENARIO HOOKS
// =============================================

/**
 * Hook to fetch all scenarios for the current project
 */
export const useScenarios = () => {
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  
  return useQuery({
    queryKey: ['scenarios', currentProject?.id],
    queryFn: async () => {
      if (!currentProject?.id) return [];
      const response = await apiClient.getScenarios(currentProject.id);
      // Ensure we always return an array
      return Array.isArray(response) ? response : (response?.data && Array.isArray(response.data) ? response.data : []);
    },
    enabled: !!currentProject?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: true
  });
};

/**
 * Hook to fetch a specific scenario with details
 */
export const useScenario = (scenarioId: number | null) => {
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  
  return useQuery({
    queryKey: ['scenario', scenarioId, currentProject?.id],
    queryFn: async () => {
      if (!scenarioId || !currentProject?.id) return null;
      return await apiClient.getScenario(scenarioId, currentProject.id) as ScenarioWithDetails;
    },
    enabled: !!scenarioId && !!currentProject?.id,
    staleTime: 3 * 60 * 1000, // 3 minutes
    retry: 1
  });
};

// =============================================
// SCENARIO MUTATIONS
// =============================================

/**
 * Hook to create a new scenario
 */
export const useCreateScenario = () => {
  const queryClient = useQueryClient();
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  
  return useMutation({
    mutationFn: async (data: ScenarioCreate): Promise<Scenario> => {
      if (!currentProject?.id) {
        throw new Error('No project selected');
      }
      
      const scenarioData = {
        ...data,
        project_id: currentProject.id
      };
      
      return await apiClient.createScenario(scenarioData);
    },
    onSuccess: (newScenario: Scenario) => {
      // Update scenarios list in cache
      queryClient.setQueryData(
        ['scenarios', currentProject?.id],
        (oldData: any = []) => {
          // Handle various response formats from Edge Functions
          let safeOldData: Scenario[] = [];
          if (Array.isArray(oldData)) {
            safeOldData = oldData;
          } else if (oldData?.data && Array.isArray(oldData.data)) {
            safeOldData = oldData.data;
          }
          return [...safeOldData, newScenario];
        }
      );
      
      // Add to individual scenario cache
      queryClient.setQueryData(['scenario', newScenario.id, currentProject?.id], newScenario);
    },
    onError: (error) => {
      console.error('Failed to create scenario:', error);
    }
  });
};

/**
 * Hook to update an existing scenario
 */
export const useUpdateScenario = () => {
  const queryClient = useQueryClient();
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ScenarioUpdate }): Promise<Scenario> => {
      if (!currentProject?.id) {
        throw new Error('No project selected');
      }
      
      return await apiClient.updateScenario(id, currentProject.id, data);
    },
    onSuccess: (updatedScenario: Scenario) => {
      // Update scenarios list in cache
      queryClient.setQueryData(
        ['scenarios', currentProject?.id],
        (oldData: any = []) => {
          // Handle various response formats from Edge Functions
          let safeOldData: Scenario[] = [];
          if (Array.isArray(oldData)) {
            safeOldData = oldData;
          } else if (oldData?.data && Array.isArray(oldData.data)) {
            safeOldData = oldData.data;
          }
          return safeOldData.map(scenario => 
            scenario.id === updatedScenario.id ? updatedScenario : scenario
          );
        }
      );
      
      // Update individual scenario cache
      queryClient.setQueryData(
        ['scenario', updatedScenario.id, currentProject?.id], 
        updatedScenario
      );
    },
    onError: (error) => {
      console.error('Failed to update scenario:', error);
    }
  });
};

/**
 * Hook to delete a scenario
 */
export const useDeleteScenario = () => {
  const queryClient = useQueryClient();
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  
  return useMutation({
    mutationFn: async (scenarioId: number): Promise<void> => {
      if (!currentProject?.id) {
        throw new Error('No project selected');
      }
      
      await apiClient.deleteScenario(scenarioId, currentProject.id);
    },
    onSuccess: (_, deletedScenarioId: number) => {
      // Remove from scenarios list in cache
      queryClient.setQueryData(
        ['scenarios', currentProject?.id],
        (oldData: any = []) => {
          // Handle various response formats from Edge Functions
          let safeOldData: Scenario[] = [];
          if (Array.isArray(oldData)) {
            safeOldData = oldData;
          } else if (oldData?.data && Array.isArray(oldData.data)) {
            safeOldData = oldData.data;
          }
          return safeOldData.filter(scenario => scenario.id !== deletedScenarioId);
        }
      );
      
      // Remove individual scenario from cache
      queryClient.removeQueries({
        queryKey: ['scenario', deletedScenarioId, currentProject?.id]
      });
    },
    onError: (error) => {
      console.error('Failed to delete scenario:', error);
    }
  });
};

// =============================================
// SCENARIO UTILITY HOOKS
// =============================================

/**
 * Hook to duplicate a scenario
 */
export const useDuplicateScenario = () => {
  const createScenario = useCreateScenario();
  const { data: scenarios = [] } = useScenarios();
  
  return useMutation({
    mutationFn: async ({ scenarioId, newName }: { scenarioId: number; newName: string }): Promise<Scenario> => {
      // Defensive array handling
      let scenariosList: Scenario[] = [];
      if (Array.isArray(scenarios)) {
        scenariosList = scenarios;
      } else if (scenarios?.data && Array.isArray(scenarios.data)) {
        scenariosList = scenarios.data;
      }
      const originalScenario = scenariosList.find(s => s.id === scenarioId);
      if (!originalScenario) {
        throw new Error('Scenario not found');
      }
      
      const duplicateData: ScenarioCreate = {
        name: newName,
        description: `Copy of ${originalScenario.description || originalScenario.name}`,
        project_id: originalScenario.project_id,
        start_date: originalScenario.start_date,
        planning_horizon_days: originalScenario.planning_horizon_days,
        optimization_objective: originalScenario.optimization_objective,
        parent_scenario_id: originalScenario.id
      };
      
      return await createScenario.mutateAsync(duplicateData);
    },
    onError: (error) => {
      console.error('Failed to duplicate scenario:', error);
    }
  });
};

/**
 * Hook to get scenarios filtered by status
 */
export const useScenariosByStatus = (status?: 'active' | 'archived' | 'template') => {
  const { data: scenarios = [], ...queryResult } = useScenarios();
  
  // Defensive array handling for filtering
  let scenariosList: Scenario[] = [];
  if (Array.isArray(scenarios)) {
    scenariosList = scenarios;
  } else if (scenarios?.data && Array.isArray(scenarios.data)) {
    scenariosList = scenarios.data;
  }
  
  const filteredScenarios = status 
    ? scenariosList.filter(scenario => scenario.status === status)
    : scenariosList;
  
  return {
    ...queryResult,
    data: filteredScenarios,
    scenarios: filteredScenarios
  };
};

/**
 * Hook to get scenario templates
 */
export const useScenarioTemplates = () => {
  return useScenariosByStatus('template');
};

/**
 * Hook to get active scenarios
 */
export const useActiveScenarios = () => {
  return useScenariosByStatus('active');
};

// =============================================
// SCENARIO STATISTICS AND ANALYTICS
// =============================================

/**
 * Hook to get scenario summary statistics
 */
export const useScenarioSummary = () => {
  const { data: scenarios = [] } = useScenarios();
  
  // Defensive array handling for summary statistics
  let scenariosList: Scenario[] = [];
  if (Array.isArray(scenarios)) {
    scenariosList = scenarios;
  } else if (scenarios?.data && Array.isArray(scenarios.data)) {
    scenariosList = scenarios.data;
  }
  
  const summary = {
    total: scenariosList.length,
    active: scenariosList.filter(s => s.status === 'active').length,
    archived: scenariosList.filter(s => s.status === 'archived').length,
    templates: scenariosList.filter(s => s.status === 'template').length,
    withOptimizations: scenariosList.filter(s => s.optimization_runs && Array.isArray(s.optimization_runs) && s.optimization_runs.length > 0).length
  };
  
  return summary;
};

/**
 * Hook to refresh scenarios data
 */
export const useRefreshScenarios = () => {
  const queryClient = useQueryClient();
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: ['scenarios', currentProject?.id]
      });
    }
  });
};

// =============================================
// EXPORT UTILITY FUNCTIONS
// =============================================

/**
 * Utility function to check if a scenario can be deleted
 */
export const canDeleteScenario = (scenario: Scenario): boolean => {
  // Can't delete if it has child scenarios or optimization runs
  const hasOptimizations = scenario.optimization_runs && scenario.optimization_runs.length > 0;
  return !hasOptimizations;
};

/**
 * Utility function to check if a scenario can be archived
 */
export const canArchiveScenario = (scenario: Scenario): boolean => {
  return scenario.status === 'active';
};

/**
 * Utility function to generate a unique scenario name
 */
export const generateScenarioName = (baseName: string, existingScenarios: Scenario[]): string => {
  // Defensive array handling
  const safeScenarios = Array.isArray(existingScenarios) ? existingScenarios : [];
  const existingNames = safeScenarios.map(s => s.name ? s.name.toLowerCase() : '');
  let counter = 1;
  let newName = baseName;
  
  while (existingNames.includes(newName.toLowerCase())) {
    newName = `${baseName} (${counter})`;
    counter++;
  }
  
  return newName;
};